// Multi-kitchen USP: never lock cart to one kitchen.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem } from '../types/index';
import { toast } from 'react-hot-toast';

export type CartKitchen = { kitchenId: string; kitchenName: string };

interface CartStore {
  items: CartItem[];
  lastAddedItem: CartItem | null;

  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  clearLastAdded: () => void;
  totalPrice: () => number;
  totalItems: () => number;
  /** Unique kitchens currently in the cart (derived — no single-kitchen lock). */
  kitchensInCart: () => CartKitchen[];
  kitchenCount: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      lastAddedItem: null,

      addItem: (item: CartItem) => {
        // 1. Price Validation
        if (!item.price || isNaN(item.price) || item.price < 0) {
          toast.error(`Invalid price for ${item.name}. Please refresh.`);
          console.error('Invalid item price:', { item });
          return;
        }

        // 2. Add or Update Item — multi-kitchen allowed; each CartItem keeps its own kitchenId/kitchenName
        set((state) => {
          const existingItem = state.items.find((i) => i.id === item.id);
          let newItems: CartItem[];

          if (existingItem) {
            newItems = state.items.map((i) =>
              i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i
            );
          } else {
            newItems = [...state.items, item];
          }

          return {
            items: newItems,
            lastAddedItem: item,
          };
        });

        toast.success(`${item.name} added to cart!`);
      },

      removeItem: (itemId: string) => {
        set((state) => ({
          items: state.items.filter((i) => i.id !== itemId),
        }));
      },

      updateQuantity: (itemId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }
        set((state) => ({
          items: state.items.map((i) =>
            i.id === itemId ? { ...i, quantity } : i
          ),
        }));
      },

      clearCart: () => set({ items: [], lastAddedItem: null }),

      clearLastAdded: () => set({ lastAddedItem: null }),

      totalPrice: () => {
        const state = get();
        return state.items.reduce((sum, item) => {
          const price = Number(item.price);
          if (isNaN(price)) return sum;
          return sum + (price * item.quantity);
        }, 0);
      },

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

      kitchensInCart: () => {
        const map = new Map<string, string>();
        for (const item of get().items) {
          const id = item.kitchenId || 'unknown';
          if (!map.has(id)) {
            map.set(id, item.kitchenName || 'Unknown Kitchen');
          }
        }
        return Array.from(map.entries()).map(([kitchenId, kitchenName]) => ({
          kitchenId,
          kitchenName,
        }));
      },

      kitchenCount: () => get().kitchensInCart().length,
    }),
    {
      name: 'user-cart-storage',
      // Drop legacy single-kitchen lock fields from persisted state
      partialize: (state) => ({
        items: state.items,
        lastAddedItem: state.lastAddedItem,
      }),
    }
  )
);
